/**
 * Card Product Swatches
 * ---------------------
 * Makes color swatches inside product cards (featured collection,
 * collection grid, etc.) functional WITHOUT touching the product
 * page, cart drawer, or any other variant pickers.
 *
 * Scope guard: only inputs inside `.card-product-swatchs` (rendered
 * exclusively by snippets/card-product.liquid) are handled. The PDP's
 * <variant-radios> / <variant-selects> live elsewhere and are untouched.
 *
 * Data source: a `<script type="application/json" class="product-card-variants-json">`
 * is rendered inside each card by card-product.liquid. The cart quantity
 * map is pulled from `/cart.js` on load and refreshed whenever a
 * PUB_SUB_EVENTS.cartUpdate event fires.
 */
(function () {
  // Idempotency guard — script may be included by multiple sections.
  if (window.__cardProductSwatchesInit) return;
  window.__cardProductSwatchesInit = true;

  var WRAPPER_SELECTOR = '.product-card-wrapper';
  var SWATCH_GROUP_SELECTOR = '.card-product-swatchs';
  var VARIANTS_JSON_SELECTOR = 'script.product-card-variants-json';

  // Cache parsed variant arrays per wrapper so we don't re-parse on every click.
  var variantCache = new WeakMap();

  // {variantId: quantityInCart} built from /cart.js. Used to mark a variant
  // as sold-out when the shopper already has the entire remaining inventory
  // sitting in their cart (policy = "deny"). Refreshed on load and whenever
  // a PUB_SUB_EVENTS.cartUpdate fires.
  var cartQuantities = {};
  var cartFetchInFlight = null;

  /**
   * Parse and cache the variant array for this card. Returns null if missing.
   */
  function getVariants(wrapper) {
    if (variantCache.has(wrapper)) return variantCache.get(wrapper);
    var script = wrapper.querySelector(VARIANTS_JSON_SELECTOR);
    if (!script) {
      variantCache.set(wrapper, null);
      return null;
    }
    try {
      var data = JSON.parse(script.textContent);
      variantCache.set(wrapper, data);
      return data;
    } catch (e) {
      variantCache.set(wrapper, null);
      return null;
    }
  }

  /**
   * Get a stable per-card identifier we can use to namespace radio
   * input names / ids so cards on the same page don't interfere.
   */
  function getCardId(wrapper) {
    var script = wrapper.querySelector(VARIANTS_JSON_SELECTOR);
    if (script && script.dataset.productId) return script.dataset.productId;
    // Fallback — extract from `card-product-{id}` class.
    var match = wrapper.className.match(/card-product-(\d+)/);
    return match ? match[1] : String(Math.random()).slice(2, 10);
  }

  /**
   * BUG FIX: every card on the page renders swatches with identical
   * `name` ("Color-1") and identical `id` (section-id-based). Same
   * radio name across the page means clicking one card's swatch
   * unchecks another card's swatch; identical ids break <label for>
   * highlighting (so the active swatch never looks selected).
   *
   * On init, prefix every swatch input id/name + matching label `for`
   * with the card's product id so each card is its own radio group.
   * Idempotent via a data flag.
   */
  function uniqifySwatchInputs(wrapper) {
    if (wrapper.dataset.swatchScopeInit === 'true') return;
    wrapper.dataset.swatchScopeInit = 'true';

    var cardId = getCardId(wrapper);
    var suffix = '--cp-' + cardId;
    var group = wrapper.querySelector(SWATCH_GROUP_SELECTOR);
    if (!group) return;

    var inputs = group.querySelectorAll('input.swatch-input__input');
    inputs.forEach(function (input) {
      var oldId = input.id;
      var newId = oldId + suffix;
      input.id = newId;
      if (input.name) input.name = input.name + suffix;

      // Update the matching <label for="oldId">.
      var label = group.querySelector('label[for="' + cssEscape(oldId) + '"]');
      if (label) label.setAttribute('for', newId);

      // The HTML parser only allows ONE checked radio per name across
      // the whole page — so before uniqification, every card except the
      // last lost its intended `checked` state. `defaultChecked` still
      // reflects the original HTML attribute, so we use it to restore
      // each card's default selection now that names are scoped.
      if (input.defaultChecked) input.checked = true;
    });
  }

  // Minimal CSS.escape polyfill — only handles the characters Dawn produces
  // (digits, dashes, letters, dots) safely.
  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  /**
   * Build a Shopify CDN srcset at common widths.
   */
  function buildSrcset(imageSrc) {
    if (!imageSrc) return '';
    var widths = [165, 360, 533, 720, 940, 1066];
    var base = imageSrc.split('?')[0];
    return widths
      .map(function (w) {
        return base + '?width=' + w + ' ' + w + 'w';
      })
      .join(', ');
  }

  /**
   * Update the primary product image to the variant's featured image.
   * No-op if the variant has no featured image.
   */
  function updateImage(wrapper, variant) {
    if (!variant || !variant.featured_image) return;
    var img = wrapper.querySelector('.card__media .media img');
    if (!img) return;
    var src = variant.featured_image.src;
    if (!src) return;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    var newSrc = src.split('?')[0] + '?width=533';
    if (img.getAttribute('src') === newSrc) return;
    img.setAttribute('src', newSrc);
    img.setAttribute('srcset', buildSrcset(src));
    if (variant.featured_image.alt) img.setAttribute('alt', variant.featured_image.alt);
  }

  /**
   * Update the rendered price block to reflect the variant.
   *
   * Uses the pre-formatted `price_formatted` / `compare_at_price_formatted`
   * strings rendered by Shopify's `money` Liquid filter on the server.
   * That way the symbol and amount are always correct for the active
   * currency / locale (including Shopify Markets conversions) — no
   * client-side currency detection or arithmetic required.
   */
  function updatePrice(wrapper, variant) {
    if (!variant || !variant.price_formatted) return;
    var priceEl = wrapper.querySelector('.card-information .price') || wrapper.querySelector('.price');
    if (!priceEl) return;

    var formatted = variant.price_formatted;
    var formattedCompare = variant.compare_at_price_formatted || null;

    priceEl.querySelectorAll('.price-item--regular').forEach(function (el) {
      // <s> tags inside the regular block carry the compare-at price.
      if (el.tagName === 'S' && formattedCompare) {
        el.textContent = formattedCompare;
      } else {
        el.textContent = formatted;
      }
    });

    priceEl.querySelectorAll('.price-item--sale, .price-item--last').forEach(function (el) {
      el.textContent = formatted;
    });

    priceEl.classList.toggle('price--sold-out', variant.available === false);
    priceEl.classList.toggle(
      'price--on-sale',
      Boolean(variant.compare_at_price && variant.compare_at_price > variant.price)
    );
  }

  /**
   * Cache the localized Add to Cart / Sold Out labels for a card the
   * first time we see it. The button is rendered by card-product.liquid
   * with stable `data-add-to-cart-text` / `data-sold-out-text` attributes
   * so we always have the right strings — even when the initial render
   * had the variant sold out (in which case the visible <span> would
   * read "Sold out" and reading from the DOM would be wrong).
   *
   * The .sold-out-message <span> is used as a final fallback for older
   * card markup that lacks the data attributes.
   */
  function cacheButtonLabels(submit) {
    if (submit.dataset.labelsCached === 'true') return;

    var addToCart = submit.dataset.addToCartText || '';
    var soldOut = submit.dataset.soldOutText || '';

    if (!soldOut) {
      var soldOutSpan = submit.querySelector('.sold-out-message');
      if (soldOutSpan) soldOut = soldOutSpan.textContent.trim();
    }

    submit.dataset.addToCartLabel = addToCart || 'Add to cart';
    submit.dataset.soldOutLabel = soldOut || 'Sold out';
    submit.dataset.labelsCached = 'true';
  }

  /**
   * Update the hidden form input + the Add to Cart button to reflect
   * the selected variant. Always sets the correct state from cached
   * labels — never reads the current button text (which might already
   * say "Sold out" from a previous add-to-cart).
   */
  function updateAvailability(wrapper, variant, swatchInput) {
    // A single card renders two forms — a desktop one (.quick-add) and a
    // mobile one (.quick-add.quick-add--mobile). Each has its own hidden
    // input + submit button, so we must update ALL of them, not just the
    // first. Using querySelector here caused the mobile button to stay
    // stuck on "Sold out" after switching to an in-stock variant.
    var hiddenInputs = wrapper.querySelectorAll('input[name="id"].product-variant-id');
    if (variant) {
      hiddenInputs.forEach(function (hiddenInput) {
        hiddenInput.value = variant.id;
        if (variant.available) {
          hiddenInput.removeAttribute('disabled');
        } else {
          hiddenInput.setAttribute('disabled', 'disabled');
        }
      });
    }

    var submits = wrapper.querySelectorAll('.quick-add__submit');
    if (!submits.length) return;

    // If the variant data is missing for some reason, fall back to the
    // visually-disabled class on the swatch as the availability hint.
    var isAvailable = variant
      ? variant.available === true
      : !swatchInput.classList.contains('visually-disabled');

    // Treat as sold out when the shopper already has the full remaining
    // tracked inventory of this variant in their cart and the store's
    // oversell policy is "deny". Skipped for untracked inventory and for
    // "continue" policy (where overselling is allowed).
    if (
      isAvailable &&
      variant &&
      variant.inventory_management &&
      variant.inventory_policy !== 'continue' &&
      typeof variant.inventory_quantity === 'number'
    ) {
      var inCart = cartQuantities[variant.id] || 0;
      if (inCart >= variant.inventory_quantity) {
        isAvailable = false;
      }
    }

    submits.forEach(function (submit) {
      cacheButtonLabels(submit);

      var primarySpan = null;
      var soldOutSpan = null;
      submit.querySelectorAll('span').forEach(function (span) {
        if (span.classList.contains('sold-out-message')) soldOutSpan = span;
        else if (!span.classList.contains('icon-wrap') && !primarySpan) primarySpan = span;
      });

      if (isAvailable) {
        // Dawn's product-form.js disables the button via BOTH `aria-disabled`
        // AND by adding `.hidden` to the primary span (when an add-to-cart
        // attempt fails because a variant just sold out). We must undo all
        // of those, otherwise switching to an in-stock swatch leaves the
        // button visually empty / still disabled.
        submit.removeAttribute('disabled');
        submit.removeAttribute('aria-disabled');
        submit.classList.remove('loading');
        if (primarySpan) {
          primarySpan.classList.remove('hidden');
          primarySpan.textContent = submit.dataset.addToCartLabel;
        }
        if (soldOutSpan) soldOutSpan.classList.add('hidden');

        // Reset product-form's internal error flag so its next submit
        // doesn't immediately re-disable the button.
        var productForm = submit.closest('product-form');
        if (productForm) productForm.error = false;
      } else {
        submit.setAttribute('disabled', 'disabled');
        submit.setAttribute('aria-disabled', 'true');
        if (primarySpan) {
          primarySpan.classList.remove('hidden');
          primarySpan.textContent = submit.dataset.soldOutLabel;
        }
        if (soldOutSpan) soldOutSpan.classList.add('hidden');
      }
    });
  }

  /**
   * Mark the chosen swatch with `active-swatch` (and clear it from any
   * sibling swatch in the same card) so themes can target the selected
   * one with CSS. Mirrors the class to the matching <label> as well so
   * styles can hook either element.
   */
  function setActiveSwatch(wrapper, input) {
    var group = wrapper.querySelector(SWATCH_GROUP_SELECTOR);
    if (!group) return;
    group.querySelectorAll('input.swatch-input__input.active-swatch').forEach(function (el) {
      el.classList.remove('active-swatch');
    });
    group.querySelectorAll('label.active-swatch').forEach(function (el) {
      el.classList.remove('active-swatch');
    });
    input.classList.add('active-swatch');
    var label = group.querySelector('label[for="' + cssEscape(input.id) + '"]');
    if (label) label.classList.add('active-swatch');
  }

  /**
   * Update the card's product links to deep-link to the chosen variant.
   */
  function updateProductLinks(wrapper, productUrl) {
    if (!productUrl) return;
    var links = wrapper.querySelectorAll('a.full-unstyled-link, a.card-product-link');
    links.forEach(function (a) {
      a.setAttribute('href', productUrl);
    });
  }

  /**
   * Main handler — a swatch input inside a card was changed.
   */
  function handleSwatchChange(input) {
    var wrapper = input.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;
    if (!input.closest(SWATCH_GROUP_SELECTOR)) return;

    // Make sure this card's inputs are scoped (in case the card was
    // injected after the initial DOMContentLoaded sync).
    uniqifySwatchInputs(wrapper);

    var variantId = parseInt(input.getAttribute('variant-id'), 10);
    var variant = null;
    if (variantId) {
      var variants = getVariants(wrapper);
      if (variants && variants.length) {
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].id === variantId) {
            variant = variants[i];
            break;
          }
        }
      }
    }

    setActiveSwatch(wrapper, input);
    updateAvailability(wrapper, variant, input);
    if (variant) {
      updateImage(wrapper, variant);
      updatePrice(wrapper, variant);
    }
    updateProductLinks(wrapper, input.getAttribute('data-product-url'));
  }

  // Single delegated change listener, scoped strictly to card swatches.
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || target.tagName !== 'INPUT') return;
    if (target.type !== 'radio') return;
    if (!target.closest(SWATCH_GROUP_SELECTOR)) return;
    handleSwatchChange(target);
  });

  /**
   * Mark out-of-stock swatches as `visually-disabled` based on real
   * variant data from the JSON island. Necessary because Dawn's
   * server-side logic relies on `value.available`, which can be wrong:
   *   - When inventory policy = "continue" (allow overselling), variants
   *     stay `available: true` at qty 0 — swatch never gets disabled.
   *   - For multi-option products (Color × Size), `value.available` for
   *     a Color is true if ANY size of that color is in stock, so a
   *     fully out-of-stock color can still look available.
   *
   * We trust `variant.available` for the per-variant truth, and also
   * cross-check `inventory_quantity` <= 0 when inventory is being
   * tracked, so cards reflect real stock at page load.
   */
  function syncSwatchAvailability(wrapper) {
    var variants = getVariants(wrapper);
    if (!variants || !variants.length) return;

    var inputs = wrapper.querySelectorAll(SWATCH_GROUP_SELECTOR + ' input.swatch-input__input');
    inputs.forEach(function (input) {
      var variantId = parseInt(input.getAttribute('variant-id'), 10);
      if (!variantId) return;

      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === variantId) {
          variant = variants[i];
          break;
        }
      }
      if (!variant) return;

      var soldOut = variant.available === false;
      // Inventory is tracked and depleted, but policy may still report
      // available = true (oversell allowed). Treat as sold out anyway.
      if (
        !soldOut &&
        variant.inventory_management &&
        typeof variant.inventory_quantity === 'number' &&
        variant.inventory_quantity <= 0
      ) {
        soldOut = true;
      }

      input.classList.toggle('visually-disabled', soldOut);
    });
  }

  /**
   * On load: scope each card's inputs, mark out-of-stock swatches, and
   * sync the form state to whatever's currently checked.
   */
  function initCards(root) {
    var scope = root || document;
    var wrappers = scope.querySelectorAll(WRAPPER_SELECTOR);
    wrappers.forEach(function (wrapper) {
      if (!wrapper.querySelector(SWATCH_GROUP_SELECTOR)) return;
      uniqifySwatchInputs(wrapper);
      syncSwatchAvailability(wrapper);
      var checked = wrapper.querySelector(SWATCH_GROUP_SELECTOR + ' input[type="radio"]:checked');
      if (checked) handleSwatchChange(checked);
    });
  }

  /**
   * Re-run availability/price/image updates on every card using the
   * currently checked swatch. Called after the cart quantity map changes
   * so buttons can flip to "Sold out" the moment a variant gets maxed
   * out in the cart — no click on the card required.
   */
  function refreshAllCards() {
    var wrappers = document.querySelectorAll(WRAPPER_SELECTOR);
    wrappers.forEach(function (wrapper) {
      if (!wrapper.querySelector(SWATCH_GROUP_SELECTOR)) return;
      var checked = wrapper.querySelector(SWATCH_GROUP_SELECTOR + ' input[type="radio"]:checked');
      if (checked) handleSwatchChange(checked);
    });
  }

  /**
   * Fetch the current cart and rebuild the {variantId: qty} map.
   * Concurrent calls share the in-flight promise so a burst of cart
   * events doesn't produce a storm of requests.
   */
  function fetchCartQuantities() {
    if (cartFetchInFlight) return cartFetchInFlight;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root.replace(/\/?$/, '/') + 'cart.js';
    cartFetchInFlight = fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        return res && res.ok ? res.json() : null;
      })
      .then(function (cart) {
        var map = {};
        if (cart && Array.isArray(cart.items)) {
          cart.items.forEach(function (item) {
            if (!item || typeof item.id !== 'number') return;
            map[item.id] = (map[item.id] || 0) + (item.quantity || 0);
          });
        }
        cartQuantities = map;
      })
      .catch(function () {
        // Network error — leave the prior map in place rather than
        // clobbering it with stale zero values.
      })
      .then(function () {
        cartFetchInFlight = null;
      });
    return cartFetchInFlight;
  }

  /**
   * Sync the cart map and re-render every card. Subscribes to the
   * theme's cartUpdate bus so add-to-cart / cart quantity changes
   * anywhere on the page immediately reflect in card buttons.
   */
  function setupCartSync() {
    fetchCartQuantities().then(refreshAllCards);

    if (
      typeof window.subscribe === 'function' &&
      typeof window.PUB_SUB_EVENTS === 'object' &&
      window.PUB_SUB_EVENTS &&
      window.PUB_SUB_EVENTS.cartUpdate
    ) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        fetchCartQuantities().then(refreshAllCards);
      });
    } else if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // pubsub.js exposes these as file-scoped bindings under strict module
      // loading; fall back to the bare identifiers if they resolve.
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          fetchCartQuantities().then(refreshAllCards);
        });
      } catch (e) {
        /* no-op — bus not available, cart-based sold-out will only
           update on the next page load. */
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initCards();
      setupCartSync();
    });
  } else {
    initCards();
    setupCartSync();
  }

  // Re-scope cards inserted via Section Rendering API (facets, load-more).
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(WRAPPER_SELECTOR)) {
          initCards(node.parentNode || document);
        } else if (node.querySelector && node.querySelector(WRAPPER_SELECTOR)) {
          initCards(node);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
